import type {
  AdjustmentType,
  ExtractDocumentResponse,
  MatchedObjectType,
  ReconciliationMatchDTO,
  ReviewQueueResponse,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

/**
 * SME-side reconciliation API client. JWT-scoped to the SME's own org.
 *
 * NOTE: only READ + safe-write (attach DOCUMENT-type match) endpoints
 * are exposed here. Confirm/reject/run/rule routes are reviewer-only
 * and would return 403 for CLIENT users — we don't even surface them.
 */

export async function getReconciliationQueue(args?: {
  monthlyCloseId?: string;
}): Promise<ReviewQueueResponse> {
  const url = args?.monthlyCloseId
    ? `${ENDPOINTS.reconciliation.queue}?monthlyCloseId=${encodeURIComponent(args.monthlyCloseId)}`
    : ENDPOINTS.reconciliation.queue;
  const { data } = await apiClient.get<ReviewQueueResponse>(url);
  return data;
}

export interface AttachReceiptArgs {
  transactionId: string;
  documentId: string;
  /** Defaults to RECEIPT — only document-backed types allowed for SMEs. */
  matchedObjectType?: Extract<
    MatchedObjectType,
    'RECEIPT' | 'EXPENSE' | 'PAYROLL' | 'TAX_PAYMENT'
  >;
  notes?: string;
  /** Optional variance adjustment when the receipt amount differs from
   *  the bank-line amount (processing fee, FX, write-off, etc.). */
  adjustment?: {
    type: AdjustmentType;
    amount: string;
    description?: string;
  };
}

/**
 * Attach a document (receipt / expense / payroll / tax-payment) to a
 * flagged transaction. The API enforces SME-allowed match types — this
 * client just defaults to RECEIPT which is the common case from the SME
 * "needs receipt" list.
 */
export async function attachReceipt(args: AttachReceiptArgs): Promise<ReconciliationMatchDTO> {
  const body = {
    transactionId: args.transactionId,
    matchedObjectId: args.documentId,
    matchedObjectType: args.matchedObjectType ?? ('RECEIPT' as const),
    notes: args.notes,
    adjustment: args.adjustment,
  };
  const { data } = await apiClient.post<ReconciliationMatchDTO>(
    ENDPOINTS.reconciliation.matches,
    body,
  );
  return data;
}

export type { ExtractDocumentResponse };

/** Extract structured fields from a Document via the SME-driven extract route. */
export async function extractDocument(documentId: string): Promise<ExtractDocumentResponse> {
  const { data } = await apiClient.post<ExtractDocumentResponse>(
    `/documents/${documentId}/extract`,
    {},
  );
  return data;
}

// ── Match-to-existing (SME proposes, reviewer approves) ────────────────────

export interface MatchTargetCandidate {
  kind: 'entity' | 'document';
  id: string;
  matchedObjectType: MatchedObjectType;
  displayName: string;
  externalRef: string | null;
  amount: string | null;
  date: string | null;
  status: string | null;
}

export interface MatchTargetsResponse {
  entities: MatchTargetCandidate[];
  documents: MatchTargetCandidate[];
}

export interface FetchMatchTargetsArgs {
  q?: string;
  types?: MatchedObjectType[];
  limit?: number;
}

export async function fetchMatchTargets(
  args: FetchMatchTargetsArgs = {},
): Promise<MatchTargetsResponse> {
  const params = new URLSearchParams();
  if (args.q) params.set('q', args.q);
  if (args.types?.length) params.set('types', args.types.join(','));
  if (args.limit) params.set('limit', String(args.limit));
  const qs = params.toString();
  const url = qs ? `${ENDPOINTS.reconciliation.matchTargets}?${qs}` : ENDPOINTS.reconciliation.matchTargets;
  const { data } = await apiClient.get<MatchTargetsResponse>(url);
  return data;
}

export interface ProposeMatchArgs {
  transactionId: string;
  matchedObjectType: MatchedObjectType;
  matchedObjectId: string;
  notes?: string;
  adjustment?: {
    type: AdjustmentType;
    amount: string;
    description?: string;
  };
}

/**
 * SME proposes a match against an existing bill/invoice/receipt in the books.
 * The backend forces SUGGESTED status for CLIENT callers on non-document
 * types — reviewer still has to confirm before it lands as RECONCILED.
 */
export async function proposeMatch(args: ProposeMatchArgs): Promise<ReconciliationMatchDTO> {
  const body = {
    transactionId: args.transactionId,
    matchedObjectType: args.matchedObjectType,
    matchedObjectId: args.matchedObjectId,
    notes: args.notes,
    adjustment: args.adjustment,
    propose: true,
  };
  const { data } = await apiClient.post<ReconciliationMatchDTO>(
    ENDPOINTS.reconciliation.matches,
    body,
  );
  return data;
}
